import { HIDDEN_ROWS, PIECE_COLORS } from "./constants";
import type { ClearResult, PieceId } from "./types";

type ParticleKind = "spark" | "shard" | "glow" | "flare";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  kind: ParticleKind;
  rotation: number;
  vr: number;
}

export interface BoardLayout {
  originX: number;
  originY: number;
  cell: number;
  width: number;
  height: number;
}

export class TetrisEffects {
  particles: Particle[] = [];
  shake = 0;
  flash = 0;
  flare = 0;
  impactX = 0;
  impactY = 0;
  clearingRows: Array<{ y: number; life: number }> = [];

  update(dt: number): void {
    const t = dt / 1000;
    this.shake = Math.max(0, this.shake - t * 18);
    this.flash = Math.max(0, this.flash - t * 3.2);
    this.flare = Math.max(0, this.flare - t * 2.4);
    this.clearingRows = this.clearingRows
      .map((row) => ({ ...row, life: row.life - t * 3 }))
      .filter((row) => row.life > 0);

    this.particles = this.particles.filter((particle) => {
      particle.life -= t;
      particle.x += particle.vx * t;
      particle.y += particle.vy * t;
      particle.vy += 520 * t;
      particle.vx *= 0.985;
      particle.rotation += particle.vr * t;
      return particle.life > 0;
    });
  }

  burstLock(
    cells: Array<{ x: number; y: number }>,
    piece: PieceId,
    layout: BoardLayout
  ): void {
    const color = PIECE_COLORS[piece].light;
    cells.forEach((cell) => {
      const { x, y } = this.cellCenter(cell.x, cell.y, layout);
      this.spawnBurst(x, y, color, 5, 0.7);
    });
    this.shake = Math.max(this.shake, 0.35);
  }

  burstHardDrop(
    cells: Array<{ x: number; y: number }>,
    piece: PieceId,
    distance: number,
    layout: BoardLayout
  ): void {
    if (cells.length === 0) return;
    const color = PIECE_COLORS[piece].base;
    const lowest = cells.reduce((max, cell) => Math.max(max, cell.y), 0);
    cells.forEach((cell) => {
      const { x, y } = this.cellCenter(cell.x, cell.y, layout);
      for (let i = 0; i < distance; i += 1) {
        this.particles.push({
          x,
          y: y - i * layout.cell * 0.65,
          vx: (Math.random() - 0.5) * 40,
          vy: 20,
          life: 0.28,
          maxLife: 0.28,
          size: Math.max(2, layout.cell * 0.12),
          color,
          kind: "glow",
          rotation: 0,
          vr: 0,
        });
      }
    });
    const impact = this.cellCenter(cells[0]?.x ?? 4, lowest, layout);
    this.impactX = impact.x;
    this.impactY = impact.y;
    this.flare = 0.7;
    this.shake = Math.max(this.shake, 1.1);
    this.spawnBurst(impact.x, impact.y, color, 16, 1.1);
  }

  burstClear(clear: ClearResult, layout: BoardLayout, piece?: PieceId): void {
    const color = piece ? PIECE_COLORS[piece].light : "#f8fafc";
    clear.rows.forEach((row) => {
      this.clearingRows.push({ y: row, life: 1 });
      for (let col = 0; col < 10; col += 1) {
        const { x, y } = this.cellCenter(col, row, layout);
        this.spawnBurst(x, y, color, clear.isTetris ? 14 : 8, 1);
        if (clear.isTetris) {
          this.particles.push({
            x,
            y,
            vx: (Math.random() - 0.5) * 80,
            vy: -180 - Math.random() * 120,
            life: 0.9,
            maxLife: 0.9,
            size: 2 + Math.random() * 3,
            color: "#fff7ed",
            kind: "flare",
            rotation: Math.random() * Math.PI,
            vr: (Math.random() - 0.5) * 8,
          });
        }
      }
    });

    this.flash = clear.isTetris ? 0.55 : 0.28;
    this.shake = clear.isTetris ? 2.4 : 1.1;
    if (clear.tSpin !== "none") {
      this.flare = 1;
      this.flash = Math.max(this.flash, 0.4);
    }
  }

  private spawnBurst(
    x: number,
    y: number,
    color: string,
    count: number,
    power: number
  ): void {
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (80 + Math.random() * 220) * power;
      const kind: ParticleKind =
        i % 4 === 0 ? "shard" : i % 3 === 0 ? "glow" : "spark";
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 40,
        life: 0.35 + Math.random() * 0.45,
        maxLife: 0.8,
        size: kind === "shard" ? 4 + Math.random() * 4 : 2 + Math.random() * 3,
        color,
        kind,
        rotation: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 14,
      });
    }
  }

  private cellCenter(
    col: number,
    row: number,
    layout: BoardLayout
  ): { x: number; y: number } {
    return {
      x: layout.originX + (col + 0.5) * layout.cell,
      y: layout.originY + (row - HIDDEN_ROWS + 0.5) * layout.cell,
    };
  }
}

export function drawParticles(
  ctx: CanvasRenderingContext2D,
  effects: TetrisEffects
): void {
  effects.particles.forEach((particle) => {
    const alpha = Math.max(0, particle.life / particle.maxLife);
    ctx.save();
    ctx.translate(particle.x, particle.y);
    ctx.rotate(particle.rotation);
    ctx.globalAlpha = alpha;
    if (particle.kind === "glow" || particle.kind === "flare") {
      const gradient = ctx.createRadialGradient(
        0,
        0,
        0,
        0,
        0,
        particle.size * 3
      );
      gradient.addColorStop(0, "#ffffff");
      gradient.addColorStop(0.35, particle.color);
      gradient.addColorStop(1, "transparent");
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(0, 0, particle.size * 3, 0, Math.PI * 2);
      ctx.fill();
    } else if (particle.kind === "shard") {
      ctx.fillStyle = particle.color;
      ctx.beginPath();
      ctx.moveTo(0, -particle.size);
      ctx.lineTo(particle.size * 0.7, particle.size);
      ctx.lineTo(-particle.size * 0.7, particle.size);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillStyle = particle.color;
      ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
    }
    ctx.restore();
  });
}
